import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useReminderQuery, useDeleteReminderMutation } from '../actions/apiSlice';

function formatDate(date) {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(date).toLocaleDateString(undefined, options);
}

function AirDate({ date }) {
  const aired = new Date(date) < new Date();
  return (
    <Box
      component="span"
      sx={{ color: aired ? 'success.light' : 'error.light', fontWeight: 700 }}
    >
      {formatDate(date)}
    </Box>
  );
}

function getBetterImageUrl(url) {
  if (!url) return '';
  return url.replace(/UX\d+/, 'UX342').replace(/QL\d+/, 'QL100');
}

function ReminderScreen() {
  const navigate = useNavigate();
  const { data: reminders, isLoading } = useReminderQuery();
  const [deleteReminder] = useDeleteReminderMutation();
  const [deletingId, setDeletingId] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [heroIdx, setHeroIdx] = useState(0);

  const heroSeries = reminders ? reminders.slice(0, 5) : [];

  useEffect(() => {
    if (heroSeries.length < 2) return;
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % heroSeries.length), 6000);
    return () => clearInterval(id);
  }, [heroSeries.length]);

  const navigateShow = (show) => {
    const q = `${show.name} ${show.year}`;
    const idParam = show.title_id ? `&imdb=${show.title_id}` : '';
    navigate(`/details/?q=${q}${idParam}`);
  };

  const handleDelete = (id) => {
    setDeletingId(id);
    deleteReminder(id).finally(() => setDeletingId(null));
  };

  const now = new Date();
  const upcoming = reminders ? reminders.filter((ep) => new Date(ep.air_date) >= now) : [];
  const aired = reminders ? reminders.filter((ep) => new Date(ep.air_date) < now) : [];

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Skeleton variant="rounded" sx={{ height: 280, mb: 3, borderRadius: 3 }} />
        <Grid container spacing={2}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={i}>
              <Skeleton variant="rounded" sx={{ width: '100%', aspectRatio: '2 / 3', borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (!reminders || reminders.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
        <Helmet>
          <title>Reminders | AirCheck</title>
        </Helmet>
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: 4,
            border: (t) => `1px dashed ${t.palette.divider}`,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            No reminders yet
          </Typography>
          <Typography color="text.secondary">
            Visit a TV show's detail page and set a reminder for an upcoming episode.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const hero = heroSeries[heroIdx];
  const list = activeTab === 'upcoming' ? upcoming : aired;

  return (
    <>
      <Helmet>
        <title>Reminders | AirCheck</title>
      </Helmet>

      {hero && (
        <Box sx={{ position: 'relative', height: { xs: 260, md: 380 }, overflow: 'hidden', mb: 4 }}>
          <Box
            key={hero.id}
            component="img"
            src={hero.full_poster_url}
            alt={hero.name}
            onClick={() => navigateShow(hero)}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              cursor: 'pointer',
              animation: 'fadeIn 1s ease',
              '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.8) 100%)',
            }}
          />
          <Container
            maxWidth="xl"
            sx={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'flex-end', pb: 4 }}
          >
            <Box sx={{ color: '#fff' }}>
              <Chip
                label="Reminder set"
                color="primary"
                size="small"
                sx={{ mb: 1 }}
              />
              <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                {hero.name}
              </Typography>
              <Typography sx={{ opacity: 0.85 }}>
                {hero.year}
              </Typography>
            </Box>

            {heroSeries.length > 1 && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ position: 'absolute', right: 16, bottom: 16 }}
              >
                <IconButton
                  size="small"
                  onClick={() => setHeroIdx((i) => (i - 1 + heroSeries.length) % heroSeries.length)}
                  sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setHeroIdx((i) => (i + 1) % heroSeries.length)}
                  sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Stack>
            )}
          </Container>
        </Box>
      )}

      <Container maxWidth="xl" sx={{ pb: 6 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 3, borderBottom: (t) => `1px solid ${t.palette.divider}` }}
        >
          <Tab value="upcoming" label={`Upcoming (${upcoming.length})`} />
          <Tab value="aired" label={`Aired (${aired.length})`} />
        </Tabs>

        <Grid container spacing={2}>
          {list.map((ep) => {
            const aired = new Date(ep.air_date) < now;
            return (
              <Grid item xs={6} sm={4} md={3} lg={2} key={ep.id}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    aspectRatio: '2 / 3',
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    transition: 'transform .25s ease',
                    '&:hover': { transform: 'translateY(-4px)' },
                    '&:hover img': { transform: 'scale(1.05)' },
                    border: (t) => `1px solid ${t.palette.divider}`,
                  }}
                  onClick={() => navigateShow(ep)}
                >
                  <Box
                    component="img"
                    src={getBetterImageUrl(ep.small_poster_url)}
                    alt={ep.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform .35s ease',
                    }}
                  />
                  <Chip
                    size="small"
                    label={aired ? 'Aired' : 'Upcoming'}
                    color={aired ? 'success' : 'primary'}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      fontWeight: 700,
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(ep.id);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      bgcolor: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                      backdropFilter: 'blur(6px)',
                      '&:hover': { bgcolor: 'rgba(229,9,20,0.9)' },
                    }}
                  >
                    {deletingId === ep.id ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RemoveCircleOutlineOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      background:
                        'linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.9) 100%)',
                      display: 'flex',
                      alignItems: 'flex-end',
                      p: 1.25,
                    }}
                  >
                    <Box sx={{ color: '#fff', width: '100%' }}>
                      <Typography variant="body2" noWrap fontWeight={700}>
                        {ep.name}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.95 }}>
                        S{ep.season}E{ep.episode} · <AirDate date={ep.air_date} />
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </>
  );
}

export default ReminderScreen;
